<template>
  <main>
    <Header :hideHelpButton="true" />
    <div class="view-main-container">
      <div class="top">
        <div class="fs-6 fw-bold mb-3">
          {{ t("CORE_APP_ONBOARDING.REGISTRATION_TAG_LOGIN_DESCRIPTION") }}
        </div>
        <span>{{
          t("CORE_APP_ONBOARDING.REGISTRATION_TAG_LOGIN_INPUT_LABEL")
        }}</span>
        <TextInput
          class="fw-semibold"
          v-model:value="registrationTagInputValue"
          @enter-press="handleContinueClick"
        />
      </div>
      <div class="bottom">
        <div class="my-3">
          <Button :text="t('CONTINUE')" :click-handler="handleContinueClick" />
        </div>
        <Button
          :secondary="true"
          :text="t('CANCEL')"
          :click-handler="handleCancelClick"
        />
      </div>
    </div>
  </main>
  <LoadingOverlay
    :loading="isLoading"
    :title="t('CORE_APP_ONBOARDING.REGISTRATION_TAG_LOADING_1')"
    :message="t('CORE_APP_ONBOARDING.REGISTRATION_TAG_LOADING_2')"
  />
  <ChatbotWrapper />
</template>

<script lang="ts">
import { defineComponent, ref } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "@/apps/core-app-onboarding/composables/useI18n";
import { setLanguage } from "@/apps/core-app-onboarding/helpers/translations.helper";
import useAsset from "@/apps/core-app-onboarding/helpers/useAsset";
import Button from "@/apps/core-app-onboarding/components/Button.vue";
import Header from "@/apps/core-app-onboarding/components/Header.vue";
import ChatbotWrapper from "@/apps/core-app-onboarding/components/ChatbotWrapper.vue";
import TextInput from "@/shared-components/common/TextInputEnhanced.vue";
import logAnalyticsEvent from "@/apps/core-app-onboarding/services/google-analytics-events.service";
import { ApplicationFromRegistrationTagResponse } from "@/apps/core-app-onboarding/types/responses";
import { AndroidInterface } from "@/apps/core-app-onboarding/services/android.service";
import { APIService as InStoreApiService } from "@/apps/core-app-onboarding/services/apiClient/api.service";
import LoadingOverlay from "@/shared-components/ui/core-app/LoadingOverlay.vue";
// CreditLine app services
import { ErrorHandler } from "@/apps/credit-line/helpers/errorHandlerWithLogger.helper";
import AnalyticEvents from "@/shared-helpers/google-analytics-events.helper";

declare const Android: AndroidInterface;

export default defineComponent({
  name: "PersonRegistrationTagLoginView",
  components: {
    Button,
    Header,
    TextInput,
    LoadingOverlay,
    ChatbotWrapper,
  },
  beforeMount() {
    setLanguage(this);
  },
  setup() {
    /* Component state variables */
    const inStoreApi = new InStoreApiService();
    const { t } = useI18n();
    const router = useRouter();
    const registrationTagInputValue = ref<string>("");
    const isLoading = ref<boolean>(false);
    const registrationTagNotFound = ref<boolean>(false);
    const storedSessionId = ref<string>(Android.getCreditLineApplicationId());
    return {
      inStoreApi,
      Android,
      isLoading,
      storedSessionId,
      router,
      registrationTagInputValue,
      registrationTagNotFound,
      useAsset,
      logAnalyticsEvent,
      t,
    };
  },
  methods: {
    hideLoader() {
      if (Android?.finishedLoading) {
        Android.finishedLoading();
      } else {
        setTimeout(() => {
          this.hideLoader();
        }, 1000);
      }
    },
    handleContinueClick() {
      this.isLoading = true;
      this.inStoreApi
        .getApplicationFromRegistrationTag(
          this.registrationTagInputValue,
          this.storedSessionId
        )
        .then((res) => {
          const applicationValidation = this.validateApplicationData(res.data);
          if (applicationValidation.isValid) {
            const applicationId = res.data.applicationId;
            this.loginApplication(
              applicationId as number,
              applicationValidation.directLogin
            );
          } else {
            this.registrationTagNotFound = true;
            setTimeout(() => {
              this.launchPersonReg();
            }, 3000);
          }
        })
        .catch((err) => {
          ErrorHandler.handleNetworkErrorAndLog(err);
          this.isLoading = false;
        });
    },
    validateApplicationData(
      data: ApplicationFromRegistrationTagResponse
      // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    ): any {
      const deviceInfo = JSON.parse(Android.getDeviceInfo());
      const applicationId = data.applicationId;
      const hasPersonUuid = data.personUuid && true;
      const matchingMakeModel = data.deviceMakeModel === deviceInfo.makeModel;
      const matchingManufacturer =
        data.deviceManufacturer === deviceInfo.manufacturer;

      // If we didn't find an application associated to the given registrationTag, proceed to regular KYC with SMILE
      if (!applicationId) {
        return {
          isValid: false,
          directLogin: false,
        };
      }

      // directLogin means we found a Play Store tester. They get to go to offers
      if (data.directLogin) {
        return {
          isValid: true,
          directLogin: true,
        };
      }

      // If the application doesn't comply with the expected values, proceed to regular KYC with SMILE
      if (!hasPersonUuid || !matchingMakeModel || !matchingManufacturer) {
        return {
          isValid: false,
          directLogin: false,
        };
      }

      /**
       * DANGER ZONE
       *
       * At this point we have identified a CL application that passed KYC and is ready for an offer.
       * We will allow the user to continue (for now), but we have no guarantees that the person behind
       * the phone is actually the same person who passed KYC, so there's a risk for fraud.
       *
       * In the future, we could implemented an Incode FaceLogin screen or flag the application for manual
       * review. We'll need to keep a close eye on usage over this feature.
       */
      return {
        isValid: true,
        directLogin: false,
      };
    },
    handleCancelClick() {
      this.router.push({
        name: "InitView",
      });
    },
    launchPersonReg() {
      this.isLoading = true;
      this.inStoreApi
        .initiatePersonRegistration({
          applicationId: this.storedSessionId,
        })
        .then((res) => {
          const personRegLaunchUrl = res?.data?.launchUrl;
          if (personRegLaunchUrl) {
            window.location.href = personRegLaunchUrl;
          }
          // ToDo: Edgecase, route to error screen
        })
        .catch((err) => {
          ErrorHandler.handleNetworkErrorAndLog(err);
          this.isLoading = false;
        });
    },
    loginApplication(applicationId: number, directLogin: boolean) {
      Android.overrideApplicationId(applicationId);
      logAnalyticsEvent(
        AnalyticEvents.CoreAppOnboarding.PersonRegTagLoginSuccess,
        "CoreAppOnboarding"
      );
      this.router.push({
        name: "PersonRegistrationCompleteView",
        query: {
          directLogin: directLogin.toString(),
        },
      });
    },
  },
  mounted() {
    // Hide Android loading overlay
    this.hideLoader();

    // Log Screen View into GA
    logAnalyticsEvent(
      AnalyticEvents.CoreAppOnboarding.PersonRegTagLoginScreenView,
      "CoreAppOnboarding"
    );
  },
});
</script>

<style lang="scss" scoped>
@import "@/apps/core-app-onboarding/styles/index.scss";
.top {
  padding: 40px 20px 0;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
}
.bottom {
  flex-grow: 0;
  padding: 20px 0;
}
</style>
